import React from 'react';

/** Room kept between the list and the screen edge, and between the list and its trigger. */
const EDGE_PX = 8;
const GAP_PX = 6;

/** Where the list sits (fixed, from the viewport's top-left). */
export type PortalMenuPlace = { top: number; left: number };

/** Which edge of the trigger the list lines up with. */
export type PortalMenuAlign = 'start' | 'end';

export interface UsePortalMenuOptions {
  /** `end` (the default): right edges aligned; `start`: left edges aligned. Either way it stays on screen. */
  align?: PortalMenuAlign;
  /** Called with `true` when the list opens and `false` when it closes. */
  onOpenChange?: (open: boolean) => void;
}

export interface UsePortalMenu<T extends HTMLElement> {
  open: boolean;
  setOpen: (open: boolean) => void;
  /** Close, returning focus to the trigger when `refocus`. */
  close: (refocus: boolean) => void;
  /** The list's place once measured (null on the first, hidden paint). */
  place: PortalMenuPlace | null;
  triggerRef: React.RefObject<T>;
  panelRef: React.RefObject<HTMLDivElement>;
  /** Arrow keys move between the list's enabled buttons. */
  onItemKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  /** The list's inline position (hidden until measured). */
  panelStyle: React.CSSProperties;
}

/**
 * A small menu whose list is portalled to the body (so a scroller or a sheet never clips it) and placed from its
 * trigger: below, or above when only above has room, following the trigger while anything scrolls or the window
 * resizes. Escape, a click outside and choosing an item close it; focus returns to the trigger, the first item
 * takes focus once the list is placed, and the arrow keys move between items. The art-style card menu and the
 * mascot picker both use it.
 */
export const usePortalMenu = <T extends HTMLElement = HTMLButtonElement>({
  align = 'end',
  onOpenChange,
}: UsePortalMenuOptions = {}): UsePortalMenu<T> => {
  const [open, setOpenState] = React.useState(false);
  const [place, setPlace] = React.useState<PortalMenuPlace | null>(null);
  const triggerRef = React.useRef<T>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  /** The first item took focus this time the list opened (following a scroll must not take it back). */
  const focusedRef = React.useRef(false);
  const onOpenChangeRef = React.useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;

  const setOpen = React.useCallback((next: boolean): void => {
    setOpenState(next);
    if (!next) {
      setPlace(null);
      focusedRef.current = false;
    }
    onOpenChangeRef.current?.(next);
  }, []);

  const close = React.useCallback(
    (refocus: boolean): void => {
      setOpen(false);
      if (refocus) triggerRef.current?.focus({ preventScroll: true });
    },
    [setOpen],
  );

  /** Place the list against the trigger: aligned to `align`, below unless only above has room. */
  const measure = React.useCallback((): void => {
    const trigger = triggerRef.current;
    const panel = panelRef.current;
    if (!trigger || !panel) return;
    const anchor = trigger.getBoundingClientRect();
    const { width, height } = panel.getBoundingClientRect();
    const roomBelow = window.innerHeight - anchor.bottom - GAP_PX - EDGE_PX;
    const roomAbove = anchor.top - GAP_PX - EDGE_PX;
    const top = roomBelow < height && roomAbove > roomBelow ? anchor.top - GAP_PX - height : anchor.bottom + GAP_PX;
    const wanted = align === 'end' ? anchor.right - width : anchor.left;
    const left = Math.min(Math.max(EDGE_PX, wanted), window.innerWidth - width - EDGE_PX);
    setPlace({ top, left });
  }, [align]);

  // Measured before paint, then kept on the trigger while anything scrolls or the window resizes.
  React.useLayoutEffect(() => {
    if (!open) return undefined;
    measure();
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [open, measure]);

  React.useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent): void => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (panelRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      close(false);
    };
    // Caught on the way down (window, capture) and stopped there, so Escape closes only the list: a sheet the
    // menu opens from listens on the document and would otherwise close too. Tab closes the list and goes on from its
    // trigger (the focus is back on it before the browser moves it), as a menu's Tab does.
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        close(true);
      } else if (event.key === 'Tab') {
        close(true);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [open, close]);

  // The first item takes focus once the list is first placed, so the menu is operable from the keyboard at once.
  React.useEffect(() => {
    if (!place || focusedRef.current) return;
    focusedRef.current = true;
    const panel = panelRef.current;
    const first =
      panel?.querySelector<HTMLButtonElement>('button[aria-checked="true"]:not(:disabled)') ??
      panel?.querySelector<HTMLButtonElement>('button:not(:disabled)');
    first?.focus({ preventScroll: true });
  }, [place]);

  const onItemKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const buttons = Array.from(panelRef.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ?? []);
    if (buttons.length === 0) return;
    const at = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const step = event.key === 'ArrowDown' ? 1 : -1;
    buttons[(at + step + buttons.length) % buttons.length].focus();
  }, []);

  const panelStyle: React.CSSProperties = place
    ? { top: place.top, left: place.left }
    : { top: 0, left: 0, visibility: 'hidden' };

  return { open, setOpen, close, place, triggerRef, panelRef, onItemKeyDown, panelStyle };
};

export default usePortalMenu;
